import { apiClient } from '../utils/api-client';
import type { LocationDocument, PlaceRows, Sponsor } from '../types/sponsor';

async function detail(response: Response, fallback: string): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { errors?: Array<{ detail?: string }> };
  return body.errors?.[0]?.detail || fallback;
}

const base = (slug: string, rows: 'sponsors' | 'documents') =>
  `/api/locations/${encodeURIComponent(slug)}/${rows}`;

export interface SponsorInput {
  name?: string;
  website_url?: string;
  /** A freshly uploaded logo; on an update, replaces the current one. */
  signed_id?: string;
}

/**
 * The panel's Sponsors page and the documents card on its Info page (issue
 * #374). Both are nested under the place's slug, and both answer with the
 * place's own rows plus, on a stage, the circuit's.
 */
class SponsorsService {
  async list(slug: string): Promise<PlaceRows<Sponsor>> {
    const response = await apiClient.get(base(slug, 'sponsors'));
    if (!response.ok) throw new Error(await detail(response, 'Failed to load sponsors'));
    return response.json();
  }

  async create(slug: string, input: SponsorInput): Promise<Sponsor> {
    const response = await apiClient.post(base(slug, 'sponsors'), input);
    if (!response.ok) throw new Error(await detail(response, 'Failed to add sponsor'));
    return (await response.json()).data;
  }

  async update(slug: string, id: string, input: SponsorInput): Promise<Sponsor> {
    const response = await apiClient.patch(`${base(slug, 'sponsors')}/${id}`, input);
    if (!response.ok) throw new Error(await detail(response, 'Failed to update sponsor'));
    return (await response.json()).data;
  }

  async remove(slug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`${base(slug, 'sponsors')}/${id}`);
    if (!response.ok) throw new Error(await detail(response, 'Failed to remove sponsor'));
  }

  async reorder(slug: string, ids: string[]): Promise<PlaceRows<Sponsor>> {
    const response = await apiClient.patch(`${base(slug, 'sponsors')}/reorder`, { sponsor_ids: ids });
    if (!response.ok) throw new Error(await detail(response, 'Failed to save the order'));
    return response.json();
  }
}

class LocationDocumentsService {
  async list(slug: string): Promise<PlaceRows<LocationDocument>> {
    const response = await apiClient.get(base(slug, 'documents'));
    if (!response.ok) throw new Error(await detail(response, 'Failed to load documents'));
    return response.json();
  }

  async create(slug: string, input: { title: string; signed_id: string }): Promise<LocationDocument> {
    const response = await apiClient.post(base(slug, 'documents'), input);
    if (!response.ok) throw new Error(await detail(response, 'Failed to add document'));
    return (await response.json()).data;
  }

  async rename(slug: string, id: string, title: string): Promise<LocationDocument> {
    const response = await apiClient.patch(`${base(slug, 'documents')}/${id}`, { title });
    if (!response.ok) throw new Error(await detail(response, 'Failed to rename document'));
    return (await response.json()).data;
  }

  async remove(slug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`${base(slug, 'documents')}/${id}`);
    if (!response.ok) throw new Error(await detail(response, 'Failed to remove document'));
  }

  async reorder(slug: string, ids: string[]): Promise<PlaceRows<LocationDocument>> {
    const response = await apiClient.patch(`${base(slug, 'documents')}/reorder`, { document_ids: ids });
    if (!response.ok) throw new Error(await detail(response, 'Failed to save the order'));
    return response.json();
  }
}

export const sponsorsService = new SponsorsService();
export const locationDocumentsService = new LocationDocumentsService();
